import { useEffect, useState } from 'react'
import api from '../utils/api'

interface ProgressData {
  report_id: string
  status: string
  current_stage: string | null
  progress_percent: number
  progress_message: string | null
  stages: Array<{
    id: string
    name: string
    order: number
    status: 'pending' | 'in_progress' | 'completed'
  }>
  last_updated: string | null
}

interface ReportProgressProps {
  reportId: string
}

export default function ReportProgress({ reportId }: ReportProgressProps) {
  const [progress, setProgress] = useState<ProgressData | null>(null)
  const [error, setError] = useState(false)

  useEffect(() => {
    let intervalId: ReturnType<typeof setInterval>

    async function fetchProgress() {
      try {
        const response = await api.get(`/report/${reportId}/progress`)
        setProgress(response.data)
        setError(false)
      } catch (err) {
        setError(true)
      }
    }

    // Initial fetch
    fetchProgress()

    // Poll every 2 seconds
    intervalId = setInterval(fetchProgress, 2000)

    return () => {
      if (intervalId) clearInterval(intervalId)
    }
  }, [reportId])

  if (error || !progress) {
    return (
      <div className="w-full max-w-3xl mx-auto text-center py-12">
        <div className="relative w-32 h-32 mx-auto mb-6">
          <div className="absolute inset-0 rounded-full border-4 border-dark-700"></div>
          <div className="absolute inset-0 rounded-full border-4 border-ginger-500 border-t-transparent animate-spin"></div>
        </div>
        <p className="text-dark-400">Connecting to scan engine...</p>
      </div>
    )
  }

  return (
    <div className="w-full max-w-3xl mx-auto">
      {/* Current action message */}
      {progress.progress_message && (
        <div className="mb-8 text-center">
          <p className="text-ginger-400 font-medium animate-pulse text-lg">
            {progress.progress_message}
          </p>
        </div>
      )}

      {/* Stage Timeline - Horizontal on desktop, vertical on mobile */}
      <div className="relative">
        {/* Desktop: Horizontal timeline */}
        <div className="hidden md:block">
          {/* Connecting line */}
          <div className="absolute top-6 left-0 right-0 h-0.5 bg-dark-700">
            <div
              className="h-full bg-gradient-to-r from-ginger-500 to-orange-500 transition-all duration-500"
              style={{
                width: `${(progress.stages.filter(s => s.status === 'completed').length / progress.stages.length) * 100}%`
              }}
            />
          </div>

          {/* Stage circles */}
          <div className="relative flex justify-between">
            {progress.stages.map((stage, index) => (
              <div key={stage.id} className="flex flex-col items-center" style={{ width: `${100 / progress.stages.length}%` }}>
                {/* Circle */}
                <div className={`relative z-10 w-12 h-12 rounded-full flex items-center justify-center transition-all duration-300 ${
                  stage.status === 'completed'
                    ? 'bg-gradient-to-br from-green-400 to-green-600 shadow-lg shadow-green-500/30'
                    : stage.status === 'in_progress'
                    ? 'bg-gradient-to-br from-ginger-400 to-orange-500 shadow-lg shadow-ginger-500/50 animate-pulse'
                    : 'bg-dark-700'
                }`}>
                  {stage.status === 'completed' ? (
                    <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
                    </svg>
                  ) : stage.status === 'in_progress' ? (
                    <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  ) : (
                    <span className="text-dark-400 font-medium">{index + 1}</span>
                  )}
                </div>

                {/* Stage name */}
                <p className={`mt-3 text-xs text-center max-w-[100px] ${
                  stage.status === 'completed'
                    ? 'text-green-400 font-medium'
                    : stage.status === 'in_progress'
                    ? 'text-ginger-400 font-medium'
                    : 'text-dark-500'
                }`}>
                  {stage.name}
                </p>
              </div>
            ))}
          </div>
        </div>

        {/* Mobile: Vertical timeline */}
        <div className="md:hidden space-y-4">
          {progress.stages.map((stage, index) => (
            <div key={stage.id} className="flex items-center gap-4">
              {/* Circle with connecting line */}
              <div className="relative">
                <div className={`w-10 h-10 rounded-full flex items-center justify-center transition-all ${
                  stage.status === 'completed'
                    ? 'bg-gradient-to-br from-green-400 to-green-600 shadow-lg shadow-green-500/30'
                    : stage.status === 'in_progress'
                    ? 'bg-gradient-to-br from-ginger-400 to-orange-500 shadow-lg shadow-ginger-500/50 animate-pulse'
                    : 'bg-dark-700'
                }`}>
                  {stage.status === 'completed' ? (
                    <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
                    </svg>
                  ) : stage.status === 'in_progress' ? (
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  ) : (
                    <span className="text-dark-400 text-sm font-medium">{index + 1}</span>
                  )}
                </div>
                {/* Vertical line */}
                {index < progress.stages.length - 1 && (
                  <div className={`absolute left-1/2 top-10 w-0.5 h-4 -translate-x-1/2 ${
                    stage.status === 'completed' ? 'bg-green-500' : 'bg-dark-700'
                  }`} />
                )}
              </div>

              {/* Stage info */}
              <div className="flex-1">
                <p className={`text-sm font-medium ${
                  stage.status === 'completed'
                    ? 'text-green-400'
                    : stage.status === 'in_progress'
                    ? 'text-ginger-400'
                    : 'text-dark-400'
                }`}>
                  {stage.name}
                </p>
                <p className="text-xs text-dark-500">
                  {stage.status === 'completed' ? 'Complete' :
                   stage.status === 'in_progress' ? 'In progress...' : 'Waiting'}
                </p>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Bottom info */}
      <div className="mt-10 p-6 bg-dark-800/50 rounded-xl border border-dark-700">
        <div className="flex items-start gap-4">
          <div className="w-10 h-10 rounded-full bg-ginger-500/20 flex items-center justify-center flex-shrink-0">
            <svg className="w-5 h-5 text-ginger-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          <div>
            <h4 className="text-white font-medium mb-1">What's happening?</h4>
            <p className="text-dark-400 text-sm">
              Our AI is scanning hundreds of data sources, analyzing your digital footprint, and identifying potential threats. This thorough analysis typically takes 10-30 minutes. You can leave this page - we'll email you when your report is ready.
            </p>
          </div>
        </div>
      </div>

      {/* Last updated */}
      {progress.last_updated && (
        <p className="text-center text-dark-500 text-xs mt-4">
          Last updated: {new Date(progress.last_updated).toLocaleTimeString()}
        </p>
      )}
    </div>
  )
}
